
import { useContext, useEffect } from "react";
import { useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { GrEdit } from "react-icons/gr";
import { useHttpNonTable } from "../../../../shared/hooks/use-httpNonTable";
import SearchInput from "../../../components/search-input/search-input";

import Spinner from "../../../../shared/components/spinner/spinner";
import "./domain-type-selector.scss";
import AuthContext from "../../../../shared/context/auth-context";
import { IEnumerationOption } from "../../../../shared/definitions/datatypes";
import { getAllDataNonTable } from "../../../../shared/utils/api";
import { validateLoadingStatusNew } from "../../../../shared/utils/utils";
import { ITableRowData } from "../../../components/data-table/data-table";
import DomainTypePopup from "./domain-type-popup";

interface IDomainTypeSelectorProps {
  onSelectDomainType: (domainTypeId: string) => void;
  isDirty: boolean;  
  setFlag: (flag: boolean) => void;
  isDomainTypeUpdated: (isUpdated: boolean) => void;
}

const DomainTypeSelector = ({onSelectDomainType, isDirty, setFlag, isDomainTypeUpdated}: IDomainTypeSelectorProps) => {
  const authCtx = useContext(AuthContext);

  const [domainTypes, setDomainTypes] = useState<ITableRowData[]>([]);
  const [selectedDomainType, setSelectedDomainType] = useState<ITableRowData | undefined>(undefined);
  const [stages, setStages] = useState<ITableRowData[]>([]);
  const [showDomainTypePopup, setShowDomainTypePopup] = useState<boolean>(false);
  const [popupDomainType, setPopupDomainType] = useState<ITableRowData | undefined>(undefined);
  
  const {
    sendRequest: getDomainTypes,
    status: getDomainTypesStatus,
    data: getDomainTypesData,
    error: getDomainTypesError
  } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, undefined, "manager");

  const {
    sendRequest: getStages,
    status: getStagesStatus,
    data: getStagesData,
    error: getStagesError
  } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, undefined, "manager");

  useEffect(() => {
    getDomainTypes("domain_types", "?order_by=name asc");
  }, []);

  useEffect(() => {
    if (getDomainTypesData && getDomainTypesStatus === "completed" && !getDomainTypesError) {
      setDomainTypes(getDomainTypesData);
      setSelectedDomainType(getDomainTypesData[0]);
    }
  }, [getDomainTypesData, getDomainTypesStatus, getDomainTypesError]);

  useEffect(() => {
    if (selectedDomainType) {
      onSelectDomainType(selectedDomainType.id);
      getStages(`domain_type/${selectedDomainType.id}/stages?order_by=position asc`);
    }
  }, [selectedDomainType]);

  useEffect(() => {
    if (getStagesData && getStagesStatus === "completed" && !getStagesError) {
      setStages(getStagesData.map((st: ITableRowData) => ({
        id: st.id,
        name: st.name,
        screen_name: st.screen_name
      })));
    }
  }, [getStagesData, getStagesStatus, getStagesError]);

  const handleOnDomainTypeSwitch = (domainType: IEnumerationOption | undefined) => {
    if (isDirty) {
      setFlag(true);
      return;
    }
    if (domainType) {
      const matchedDomainType = domainTypes.find((dt: ITableRowData) => dt.id === domainType.id);
      setSelectedDomainType(matchedDomainType);
    }
  };

  const handleDomainTypePopupClosed = (result: ITableRowData | undefined) => {
    setShowDomainTypePopup(false);
    setPopupDomainType(undefined);
    if (result) {
      if (domainTypes.some((dt: ITableRowData) => dt.id === result.id)) {
        setDomainTypes([...domainTypes.map((dt: ITableRowData) => dt.id === result.id ? result : dt)]);
      } else {
        setDomainTypes([...domainTypes, result]);
      }
      setSelectedDomainType(result);
      onSelectDomainType(result.id);
    }
  };

  const handleDomainTypeUpdated = (isUpdated: boolean) => {
    if (isUpdated) {
      isDomainTypeUpdated(isUpdated);
    }
  };

  return <div className="domainTypeSelector">
    {validateLoadingStatusNew([
      getDomainTypesStatus,
      getStagesStatus
    ]) && <Spinner />}
    <DomainTypePopup
      show={showDomainTypePopup}
      domainType={popupDomainType}
      onPopupClosed={handleDomainTypePopupClosed}
      isDomainTypeUpdated={handleDomainTypeUpdated}
    />
    <div className="domainTypeSelector-domainTypeBarHeader">
      <span className="domainTypeSelector-domainActions">
        <div onClick={() => {
          setPopupDomainType(selectedDomainType);
          setShowDomainTypePopup(true);
        }}><GrEdit /></div>
        <div onClick={() => {
          setPopupDomainType(undefined);
          setShowDomainTypePopup(true);
        }}><AiOutlinePlus /></div>
      </span>
      <div className='domainTypeSelector-searchDomainInput'>
        <SearchInput
          label=''
          inputKey='domain'
          required={true}
          data={domainTypes.map((dt: ITableRowData) => ({ id: dt.id, name: dt.name }))}
          selectedOption={selectedDomainType?.id || ""}
          handleOnChange={handleOnDomainTypeSwitch}
        />
      </div>
    </div>
    <div className="domainTypeSelector-stagesList">
      {stages.map((st: ITableRowData) => (
        <div className="domainTypeSelector-stage"
          onClick={() => {
            setPopupDomainType(selectedDomainType);
            setShowDomainTypePopup(true);}
          }
          key={st.id}>
          <div className="domainTypeSelector-stageName">
            {st.name}
          </div>
          <div className="domainTypeSelector-stageScreenName">{st.screen_name}</div>
        </div>
      ))}
    </div>
  </div>;
};

export default DomainTypeSelector;